import axios from 'axios';

export function getApiBaseUrl() {
  const envBase = (process.env.REACT_APP_API_URL || '').trim();

  // If we're on HTTPS, never allow an HTTP API base (mixed content).
  if (window.location.protocol === 'https:' && envBase && envBase.startsWith('http://')) {
    return window.location.origin;
  }

  // If we're running the frontend directly on a dev port (no nginx proxy), default API to :8000.
  // This keeps local/prod flows both working without forcing an env override.
  if (!envBase && ['3000', '3003'].includes(String(window.location.port || ''))) {
    return `http://${window.location.hostname}:8000`;
  }

  return envBase || window.location.origin;
}

export const API_BASE_URL = getApiBaseUrl();

/** Apply auth + group headers on every axios request (direct calls included). */
function applyAuthHeaders(config) {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    delete config.headers.Authorization;
  }
  config.headers['X-Group-Id'] = localStorage.getItem('activeGroupId') || '1';
  return config;
}

// Global group scoping for all tabs (components use axios directly too)
export function setActiveGroupId(groupId) {
  const gid = String(groupId || '1');
  localStorage.setItem('activeGroupId', gid);
  axios.defaults.headers.common['X-Group-Id'] = gid;
}

/** Persist token after login (Settings tab). */
export function setAuthToken(token) {
  if (token) {
    localStorage.setItem('authToken', token);
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    localStorage.removeItem('authToken');
    delete axios.defaults.headers.common.Authorization;
  }
}

// Initialize defaults once
(() => {
  const gid = localStorage.getItem('activeGroupId') || '1';
  axios.defaults.headers.common['X-Group-Id'] = gid;

  const token = localStorage.getItem('authToken');
  if (token) {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  }
})();

// Fresh auth/group on every request (tabs stay in sync after login or group switch).
axios.interceptors.request.use(applyAuthHeaders, (error) => Promise.reject(error));

// Ensure *direct* axios calls also bounce to Settings on 401 (many components use axios directly).
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      if ((window.location.hash || '').replace('#', '') !== 'settings') {
        window.location.hash = '#settings';
      }
    }
    return Promise.reject(error);
  }
);

// Create axios instance with default configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token if available
api.interceptors.request.use(applyAuthHeaders, (error) => Promise.reject(error));

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('authToken');
      // Route to Settings where the login UI exists.
      if ((window.location.hash || '').replace('#', '') !== 'settings') {
        window.location.hash = '#settings';
      }
    }
    return Promise.reject(error);
  }
);

// Authentication API calls
export const authAPI = {
  login: (credentials) => api.post('/api/auth/login', credentials),
  getCurrentUser: () => api.get('/api/users/me/'),
};

// Doctors API calls
export const doctorsAPI = {
  getAll: () => api.get('/api/doctors/'),
  getById: (id) => api.get(`/api/doctors/${id}`),
  create: (doctor) => api.post('/api/doctors/', doctor),
  update: (id, doctor) => api.put(`/api/doctors/${id}`, doctor),
  delete: (id) => api.delete(`/api/doctors/${id}`),
};

// Schedules API calls - Updated for Node.js backend
export const schedulesAPI = {
  getAll: () => api.get('/api/schedules/'),
  getById: (id) => api.get(`/api/schedules/${id}`),
  create: (schedule) => api.post('/api/schedules/', schedule),
  update: (id, schedule) => api.put(`/api/schedules/${id}`, schedule),
  delete: (id) => api.delete(`/api/schedules/${id}`),
  getByDateRange: (startDate, endDate) =>
    api.get(`/api/schedules/?start_date=${startDate}&end_date=${endDate}`),
};

// Time-off API calls
export const timeOffAPI = {
  getAll: () => api.get('/api/timeoff/'),
  create: (timeOff) => api.post('/api/timeoff/', timeOff),
  update: (id, timeOff) => api.put(`/api/timeoff/${id}`, timeOff),
  delete: (id) => api.delete(`/api/timeoff/${id}`),
};

// AI Optimization API calls - Updated for Node.js backend
export const aiAPI = {
  optimize: (params) => api.post('/api/ai/optimize', params),
  getConflicts: () => api.get('/api/ai/conflicts'),
  getForecast: (year, month) => api.get(`/api/ai/forecast/${year}/${month}`),
  optimizeSimple: () => api.get('/api/ai/optimize-simple'),
  predictBusy: () => api.get('/api/ai/predict-busy'),
  test: () => api.get('/api/ai/test'),
};

// Duty Requests API calls
export const dutyRequestsAPI = {
  getAll: () => api.get('/api/duty-requests/'),
  getById: (id) => api.get(`/api/duty-requests/${id}`),
  create: (request) => api.post('/api/duty-requests/', request),
  update: (id, request) => api.put(`/api/duty-requests/${id}`, request),
  delete: (id) => api.delete(`/api/duty-requests/${id}`),
  approve: (id) => api.post(`/api/duty-requests/${id}/approve`),
  reject: (id) => api.post(`/api/duty-requests/${id}/reject`),
};

// Locations/Hospitals API calls - Updated for Node.js backend
export const locationsAPI = {
  getAll: () => api.get('/api/locations/'),
  getById: (id) => api.get(`/api/locations/${id}`),
  create: (location) => api.post('/api/locations/', location),
  update: (id, location) => api.put(`/api/locations/${id}`, location),
  delete: (id) => api.delete(`/api/locations/${id}`),
};

// Meta API calls - Updated for Node.js backend
export const metaAPI = {
  getAvailability: () => api.get('/api/ai/conflicts'),
  getAnalytics: () => api.get('/api/status'),
  getSettings: () => api.get('/api/status'),
};

// Health check
export const healthAPI = {
  getStatus: () => api.get('/health'),
};

export default api;
